// Libs
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
// Components
import { Tooltip } from '@material-ui/core';
import classNames from 'classnames';

// Context
import { useComponentsPool } from '../../../ComponentsPool';
import Icon from '../../layout/components/Icon';
import { loadChatbaseScript, loadHubSpotScript } from '../../core/helper';

const HelpMenu = props => {
  const { Component } = useComponentsPool();
  const [t] = useTranslation();
  const [helpClicked, setHelpClicked] = useState(false);
  const [hubspotReady, setHubspotReady] = useState(false);
  const currentUser = useSelector(state => state.users.currentUser);
  const hubspotVisitorToken = useSelector(state => state.users.hubspotVisitorToken);
  const currentUserHMACChatbase = useSelector(state => state.users.currentUserHMACChatbase);
  const workspaceSettings = useSelector(state => state.workspace.settings);

  useEffect(() => {
    // Ne pas charger HubSpot sur les pages QR code
    if (props.isDemo || window.location.pathname.includes('/qrcodes/') || window.location.pathname.includes('/nfc-tags/')) return;

    loadHubSpotScript(HUBSPOT_PORTAL_ID)
      .then(() => {
        if (window.HubSpotConversations) {
          window.HubSpotConversations.on('widgetClosed', () => {
            // Masquer le conteneur quand le widget est fermé (via style inline)
            const container = document.getElementById('hubspot-messages-iframe-container');
            if (container) container.style.setProperty('display', 'none', 'important');
            setHelpClicked(false);
          });
          setHubspotReady(true);
        }
      });

    return () => {
      if (window.HubSpotConversations) {
        window.HubSpotConversations.widget.remove();
      }
    };
  }, []);

  useEffect(() => {
    // Configurer l'identification HubSpot (le widget sera chargé au premier clic sur "?")
    if (!props.isDemo && hubspotReady && window.HubSpotConversations) {
      if (hubspotVisitorToken && currentUser?.id) {
        window.hsConversationsSettings = {
          ...window.hsConversationsSettings,
          identificationEmail: currentUser?.email,
          identificationToken: hubspotVisitorToken,
        };
        // Si le widget est déjà chargé (retour sur la page), rafraîchir l'identification
        const widgetStatus = window.HubSpotConversations.widget.status();
        if (widgetStatus.loaded) {
          window.HubSpotConversations.widget.refresh();
        }
      }
    }
  }, [currentUser, hubspotVisitorToken, hubspotReady]);

  useEffect(() => {
    loadChatbaseScript(workspaceSettings.name)
      .then(() => {
        console.log('chatbase type', typeof window.chatbase)
        if (typeof window.chatbase === 'function') {
          // Enregistrer les callbacks seulement si Intercom est correctement chargé
          console.log('chatbase is initialized')
        } else {
          console.warn('chatbase is not initialized properly.');
        }
      });

    // Nettoyage : arrêt de chatbase et suppression des callbacks lorsque le composant est démonté
    return () => {
      if (typeof window.chatbase === 'function') {
        window.chatbase('close');
        console.log('chatbase closed')
      }
    };
  }, []);

  useEffect(() => {
    // Initialisation de chatbase pour l'identity tracking
    if (typeof window.chatbase === 'function') {
      if (currentUserHMACChatbase && currentUser?.id) {

        window.chatbase("identify", {
          user_id: currentUser?.id,
          user_hash: currentUserHMACChatbase, // this is the hash of the user_id, should be generated on the server
          user_metadata: {
            "name": `${currentUser?.first_name} ${currentUser?.last_name}`,
            "email": currentUser?.email,
            "role": currentUser?.workspace_role?.workspace_role_name,
            "workspace": workspaceSettings.name,
          }
        });
      }
    }
  }, [currentUser, currentUserHMACChatbase]);

  // Pour l'option Chatbot IA dans les QR Codes
  if (props.isDemo) {
    return (
      <div className={'qrcode-mode-button'}>
        <div className={classNames(['qrcode-mode-button-left-part qrcode-mode-button-larger-icon'])}>
          <Icon type={'chatbot'} />
        </div>
        <div
          role={'button'}
          tabIndex={-1}
          className={classNames(['qrcode-mode-button-right-part help-icon', { 'help-icon-disabled': !hubspotVisitorToken }])}
          onClick={() => {
            if (window.HubSpotConversations) {
              window.HubSpotConversations.widget.close();
            }
            window.chatbase.open()
            setHelpClicked(!helpClicked)
          }}
        >
          <p>{'Poser une question à l\'IA'}</p>
          <div className={'qrcode-arrow-back'}>
            <Icon type="ArrowForwardIcon" />
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <Tooltip
        className={'tooltip_edit_alert'}
        classes={{ tooltip: 'table-tooltip' }}
        placement={'bottom'}
        title={hubspotVisitorToken ? t('common:help') : t('common:help_not_working')}
      >
        <div
          className={classNames(['help-icon', { 'help-icon-disabled': !hubspotVisitorToken }])}
          onClick={() => {
            if (hubspotVisitorToken && window.HubSpotConversations) {
              if (helpClicked) {
                window.HubSpotConversations.widget.close();
                // Le callback widgetClosed gère le masquage et setHelpClicked(false)
              } else {
                const widgetStatus = window.HubSpotConversations.widget.status();
                if (widgetStatus.loaded) {
                  const container = document.getElementById('hubspot-messages-iframe-container');
                  if (container) {
                    container.style.removeProperty('display');
                    container.style.setProperty('right', 'auto', 'important');
                    container.style.setProperty('left', '16px', 'important');
                  }
                  window.HubSpotConversations.widget.open();
                } else {
                  // Premier clic : charger le widget puis l'ouvrir dès qu'il est prêt
                  window.HubSpotConversations.on('widgetLoaded', () => {
                    const container = document.getElementById('hubspot-messages-iframe-container');
                    if (container) {
                      container.style.setProperty('right', 'auto', 'important');
                      container.style.setProperty('left', '16px', 'important');
                    }
                    window.HubSpotConversations.widget.open();
                  });
                  window.HubSpotConversations.widget.load();
                }
                setHelpClicked(true);
              }
            }
          }}
        >
          <Component componentName={'Icon'} type={helpClicked ? 'helpInterrogationMarkFilled' : 'helpInterrogationMark'} />

        </div>
      </Tooltip>
    </>
  );
};

export default HelpMenu;
